import { cva, type VariantProps } from 'class-variance-authority';
import { AlertDialog as AlertDialogPrimitive, Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentPropsWithoutRef, HTMLAttributes, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useControllableState } from '../../../lib/hooks/useControllableState';
import { Icon } from '../../../lib/icon/Icon';
import { cn } from '../../../lib/utils';
import { buttonVariants } from '../button';

interface PopupContextValue {
  dismissable: boolean;
  requestClose: () => void;
}

const PopupContext = createContext<PopupContextValue>({
  dismissable: true,
  requestClose: () => undefined,
});

function usePopupContext() {
  return useContext(PopupContext);
}

function pickPrimitive<T>(dismissable: boolean, dialogComponent: T, alertComponent: unknown): T {
  return (dismissable ? dialogComponent : alertComponent) as T;
}

export const popupCloseButtonClassName = cn(
  'flex size-8 items-center justify-center rounded-full bg-button-ghost p-1 transition-colors hover:bg-button-ghost-hover',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'active:bg-button-ghost-active disabled:pointer-events-none',
);

function usePopupPrimitive<T>(dialogComponent: T, alertComponent: unknown): T {
  const { dismissable } = usePopupContext();
  return pickPrimitive(dismissable, dialogComponent, alertComponent);
}

interface PopupProps {
  dismissable?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
}

function Popup({ dismissable = true, open, defaultOpen, onOpenChange, children }: PopupProps) {
  const [resolvedOpen, setOpen] = useControllableState({ value: open, defaultValue: defaultOpen ?? false, onChange: onOpenChange });
  const requestClose = useCallback(() => setOpen(false), [setOpen]);
  const contextValue = useMemo(() => ({ dismissable, requestClose }), [dismissable, requestClose]);

  const Root = pickPrimitive(dismissable, DialogPrimitive.Root, AlertDialogPrimitive.Root);
  return (
    <PopupContext.Provider value={contextValue}>
      <Root open={resolvedOpen} onOpenChange={setOpen}>
        {children}
      </Root>
    </PopupContext.Provider>
  );
}

function PopupTrigger({ ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>) {
  const Trigger = usePopupPrimitive(DialogPrimitive.Trigger, AlertDialogPrimitive.Trigger);
  return <Trigger {...props} />;
}

function PopupPortal(props: ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>) {
  const Portal = usePopupPrimitive(DialogPrimitive.Portal, AlertDialogPrimitive.Portal);
  return <Portal {...props} />;
}

function PopupOverlay({ className, ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>) {
  const Overlay = usePopupPrimitive(DialogPrimitive.Overlay, AlertDialogPrimitive.Overlay);
  return (
    <Overlay
      className={cn(
        'fixed inset-0 z-50 bg-black/80 duration-500 ease-out',
        'data-[state=open]:fade-in-0 data-[state=open]:animate-in',
        'data-[state=closed]:fade-out-0 data-[state=closed]:animate-out',
        className,
      )}
      {...props}
    />
  );
}

type PopupContentProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  showClose?: boolean;
  closeLabel?: string;
  overlayClassName?: string;
};

function PopupContent({
  className,
  children,
  showClose = true,
  closeLabel,
  overlayClassName,
  onEscapeKeyDown,
  onPointerDownOutside,
  onInteractOutside,
  ...props
}: PopupContentProps) {
  const { dismissable } = usePopupContext();
  const Content = usePopupPrimitive(DialogPrimitive.Content, AlertDialogPrimitive.Content);

  return (
    <PopupPortal>
      <PopupOverlay className={overlayClassName} />
      <Content
        className={cn(
          'fixed top-[50%] left-[50%] z-50 grid w-[calc(100%-24px)] max-w-dialog translate-x-[-50%] translate-y-[-50%] gap-4 rounded-card border bg-background p-6 shadow-popover duration-500 ease-out',
          'data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:animate-in',
          'data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:animate-out',
          className,
        )}
        onEscapeKeyDown={(event) => {
          onEscapeKeyDown?.(event);
          if (!dismissable) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          onPointerDownOutside?.(event);
          if (!dismissable) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          onInteractOutside?.(event);
          if (!dismissable) {
            event.preventDefault();
          }
        }}
        {...props}
      >
        {children}
        {dismissable && showClose && closeLabel ? (
          <PopupClose variant="icon" aria-label={closeLabel} className="absolute top-4 right-4" />
        ) : null}
      </Content>
    </PopupPortal>
  );
}

type PopupCloseProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Close> &
  ({ variant: 'icon'; 'aria-label': string } | { variant?: undefined });

function PopupClose({ variant, className, children, ...props }: PopupCloseProps) {
  const Comp = usePopupPrimitive(DialogPrimitive.Close, AlertDialogPrimitive.Cancel);
  if (variant !== 'icon') {
    return (
      <Comp className={className} {...props}>
        {children}
      </Comp>
    );
  }
  return (
    <Comp className={cn(popupCloseButtonClassName, className)} {...props}>
      {children ?? <Icon name="close" />}
    </Comp>
  );
}

function PopupHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-4 text-left', className)} {...props} />;
}

function PopupTitle({ className, ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  const Title = usePopupPrimitive(DialogPrimitive.Title, AlertDialogPrimitive.Title);
  return <Title className={cn('font-semibold text-heading-sm leading-heading-sm', className)} {...props} />;
}

function PopupDescription({ className, ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) {
  const Description = usePopupPrimitive(DialogPrimitive.Description, AlertDialogPrimitive.Description);
  return <Description className={cn('text-base text-subtle-foreground', className)} {...props} />;
}

function PopupAction({ className, onClick, ...props }: ComponentPropsWithoutRef<'button'>) {
  const { requestClose } = usePopupContext();

  async function handleClick(event: Parameters<NonNullable<typeof onClick>>[0]) {
    try {
      await onClick?.(event);
      requestClose();
    } catch (error) {
      // The consumer's onClick owns surfacing its own error state; don't close the popup on failure.
      if (import.meta.env.DEV) {
        // biome-ignore lint/suspicious/noConsole: dev-only surfacing so a bug unrelated to the "keep dialog open on failure" contract isn't invisible.
        console.error('PopupAction onClick rejected:', error);
      }
    }
  }

  return <button type="button" className={cn(buttonVariants(), className)} onClick={handleClick} {...props} />;
}

type PopupCancelVariant = 'outline' | 'ghost';

function PopupCancel({
  className,
  variant,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Close> & { variant?: PopupCancelVariant }) {
  const { dismissable } = usePopupContext();
  const resolvedVariant = variant ?? (dismissable ? 'outline' : 'ghost');
  const Comp = usePopupPrimitive(DialogPrimitive.Close, AlertDialogPrimitive.Cancel);
  return <Comp className={cn(buttonVariants({ variant: resolvedVariant }), className)} {...props} />;
}

const popupFooterVariants = cva('flex w-full gap-3', {
  variants: {
    buttons: {
      none: 'hidden',
      primary: 'flex-col sm:flex-row sm:justify-end sm:gap-2 [&>*]:w-full sm:[&>*]:w-auto sm:[&>*]:shrink-0',
      secondary: 'flex-col sm:flex-row sm:justify-end sm:gap-2 [&>*]:w-full sm:[&>*]:w-auto sm:[&>*]:shrink-0',
      'primary-secondary': 'flex-col sm:flex-row sm:gap-2 [&>*]:flex-1',
      'secondary-primary': 'flex-col sm:flex-row sm:gap-2 [&>*]:flex-1',
      'primary-primary': 'flex-col sm:flex-row sm:gap-2 [&>*]:flex-1',
      'primary-textlink':
        'flex-col items-center gap-3 sm:items-end [&>*:first-child]:w-full sm:[&>*:first-child]:w-auto sm:[&>*:first-child]:shrink-0',
    },
  },
  defaultVariants: {
    buttons: 'primary-secondary',
  },
});

type PopupFooterButtons = NonNullable<VariantProps<typeof popupFooterVariants>['buttons']>;

interface PopupFooterAction {
  label: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

interface PopupFooterPrimaryAction extends Omit<PopupFooterAction, 'onClick'> {
  onClick?: () => void | Promise<void>;
  variant?: 'default' | 'danger';
}

interface PopupFooterProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof popupFooterVariants> {
  secondaryAction?: PopupFooterAction;
  primaryAction?: PopupFooterPrimaryAction;
  textlink?: PopupFooterAction;
}

function resolveButtonsVariant(
  secondaryAction?: PopupFooterAction,
  primaryAction?: PopupFooterPrimaryAction,
  textlink?: PopupFooterAction,
) {
  if (textlink) {
    return 'primary-textlink';
  }
  if (secondaryAction && primaryAction) {
    return 'secondary-primary';
  }
  if (primaryAction) {
    return 'primary';
  }
  if (secondaryAction) {
    return 'secondary';
  }
  return 'none';
}

function PopupFooter({ className, buttons, secondaryAction, primaryAction, textlink, children, ...props }: PopupFooterProps) {
  const hasActionProps = secondaryAction || primaryAction || textlink;
  const resolvedButtons = buttons ?? (hasActionProps ? resolveButtonsVariant(secondaryAction, primaryAction, textlink) : undefined);

  return (
    <div className={cn(popupFooterVariants({ buttons: resolvedButtons }), className)} {...props}>
      {children ?? (
        <>
          {secondaryAction ? (
            <PopupCancel onClick={secondaryAction.onClick} disabled={secondaryAction.disabled}>
              {secondaryAction.label}
            </PopupCancel>
          ) : null}
          {primaryAction ? (
            <PopupAction
              className={primaryAction.variant === 'danger' ? buttonVariants({ variant: 'destructive' }) : undefined}
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </PopupAction>
          ) : null}
          {textlink ? (
            <button
              type="button"
              onClick={textlink.onClick}
              disabled={textlink.disabled}
              className="text-sm text-subtle-foreground underline underline-offset-4 disabled:opacity-40"
            >
              {textlink.label}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

export type { PopupCancelVariant, PopupCloseProps, PopupFooterAction, PopupFooterButtons, PopupFooterPrimaryAction, PopupProps };
export {
  Popup,
  PopupAction,
  PopupCancel,
  PopupClose,
  PopupContent,
  PopupDescription,
  PopupFooter,
  PopupHeader,
  PopupOverlay,
  PopupPortal,
  PopupTitle,
  PopupTrigger,
  popupFooterVariants,
};
